import * as React from "react";
import { graphql, Script } from "gatsby";
import fallbackOpenGraph from "@images/easymoney-fallback-opengraph-image.png";

export interface SeoPropTypes {
  metadata__title_tag: { value: string };
  metadata__meta_description: { value: string };
  metadata__open_graph_image?: { value: [{ url: string }] };
  metadata__noindex?: { value: [{ codename: string }] };
  metadata__page_schema?: { value: string };
  metadata__redirect_page_to?: { value: string };
}

export const SEO = ({
  metadata__title_tag,
  metadata__meta_description,
  metadata__open_graph_image,
  metadata__noindex,
  metadata__page_schema,
  metadata__redirect_page_to,
}: SeoPropTypes) => {
  const ogImageUrl =
    metadata__open_graph_image?.value[0]?.url || fallbackOpenGraph;
  const noIndex = metadata__noindex?.value[0]?.codename;
  const renderArticleSchema = () => metadata__page_schema?.value || "";
  const redirectLink = metadata__redirect_page_to?.value || null;
  if (typeof window !== "undefined" && redirectLink) {
    window.location.href = redirectLink;
    return null;
  } else {
    return (
      <>
        <meta charSet="utf-8" />
        <title>{metadata__title_tag.value}</title>
        <meta
          name="description"
          property="og:description"
          content={metadata__meta_description.value}
        />
        <meta property="og:title" content={metadata__title_tag.value} />
        <meta property="og:image" content={ogImageUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={metadata__title_tag.value} />
        <meta
          name="twitter:description"
          content={metadata__meta_description.value}
        />
        <meta name="twitter:image" content={ogImageUrl} />
        {noIndex !== "false" && <meta name="robots" content={noIndex} />}
        {metadata__page_schema && !!renderArticleSchema() && (
          <script type="application/ld+json">{renderArticleSchema()}</script>
        )}
      </>
    );
  }
};

export default SEO;

// Data Types for Different Page Types
export type SeoDataTypeDefault = {
  data: {
    kontentItemDefaultPage: { elements: SeoPropTypes };
  };
};

export type SeoDataTypeFaq = {
  data: {
    kontentItemFaqPage: { elements: SeoPropTypes };
  };
};

export type SeoDataTypeBlogPage = {
  data: {
    kontentItemBlogPost: { elements: SeoPropTypes };
  };
};

export type SeoDataTypeBlogTopic = {
  data: {
    kontentItemBlogTopic: { elements: SeoPropTypes };
  };
};

export type SeoDataTypeStoreLocatorPage = {
  data: {
    kontentStoreLocatorPage: { elements: SeoPropTypes };
  };
};

export type SeoDataTypeStoreListPage = {
  data: {
    kontentStoreLocatorPage: { elements: SeoPropTypes };
  };
};

// Query Fragment for Default Page Type
export const query = graphql`
  fragment Seo on kontent_item_default_page {
    elements {
      metadata__title_tag {
        value
      }
      metadata__meta_description {
        value
      }
      metadata__open_graph_image {
        value {
          url
        }
      }
      metadata__noindex {
        value {
          codename
        }
      }
      metadata__page_schema {
        value
      }
      metadata__redirect_page_to {
        value
      }
    }
  }
`;

// Query Fragment for FAQ Page Type
export const queryFaq = graphql`
  fragment SeoFaq on kontent_item_faq_page {
    elements {
      metadata__title_tag {
        value
      }
      metadata__meta_description {
        value
      }
      metadata__open_graph_image {
        value {
          url
        }
      }
      metadata__noindex {
        value {
          codename
        }
      }
    }
  }
`;

// Query Fragment for Store List Page Type
export const fragmentStoreList = graphql`
  fragment SeoStoreList on kontent_item_store_list_page {
    elements {
      metadata__title_tag {
        value
      }
      metadata__meta_description {
        value
      }
      metadata__noindex {
        value {
          codename
        }
      }
      metadata__open_graph_image {
        value {
          width
          url
          height
        }
      }
    }
  }
`;

// Query Fragment for Store Locator Page Type
export const fragmentStoreLocator = graphql`
  fragment SeoStoreLocator on kontent_item_store_locator_page {
    elements {
      metadata__title_tag {
        value
      }
      metadata__meta_description {
        value
      }
      metadata__noindex {
        value {
          codename
        }
      }
      metadata__open_graph_image {
        value {
          width
          url
          height
        }
      }
    }
  }
`;
